import { LitElement, html, css } from 'lit';
import { accessibility, grid } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { EventManager } from '../../utilities/events';
import { ContextConsumer } from '@lit/context';
import { organisationContext } from '../../utilities/context';
import { decodeEntities, checkFormLock, getUrlFromFileId } from '../../utilities/formsUtilities';
import { dateToMaybeRange } from '../../utilities/eventUtilities';
import { AllInResponsiveTable } from '../Utilities/AllInResponsiveTable';
import { AllInOrganisationConsumer } from '../Utilities/AllInOrganisationConsumer';
import { AllInSessionStorage } from '../Utilities/AllInSessionStorage';
import { arrowBack } from '../../utilities/icons';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
export class AllInMembers extends AllInResponsiveTable(AllInSessionStorage(AllInOrganisationConsumer(LitElement))) {
    static properties = {
        name: { type: String },
        page: { type: Number, reflect: true },
        _loading: { state: true, type: Boolean },
        _memberData: { state: true, type: Array },
        _searchResults: { state: true, type: Array },
        _hasSearch: { state: true, type: Boolean },
    };
    constructor() {
        super();
        this._loading = true;
    }
    async connectedCallback() {
        super.connectedCallback();
        document.querySelector('.wp-block-ace-content')?.classList.add('switch-to-one-column');
        this.eventManager.listen(`allin-change-page-${this.name}`, this._handleChangePage.bind(this));
        this.eventManager.listen(`allin-search-results-${this.name}`, this._handleSearchResults.bind(this));
        this.eventManager.listen(`allin-search-members-clear-${this.name}`, this._handleClearSearch.bind(this));
        if (!this.organisation) {
            this.organisation = this._getOrganisationById(this.currentOrg);
        }
        this._loading = true;
        if (this.organisation && this.organisation.hasOwnProperty('members') && this.organisation.members.hasOwnProperty(`page_${this.page}`)) {
            this._memberData = this.organisation.members[`page_${this.page}`];
            this._loading = false;
        }
        if (this.currentOrg) {
            this._maybeGetMembersFromSessionStorage();
            if (!this.members?.[this.currentOrg]?.[`page_${this.page}`]) {
                await this._fetchMembers();
            }
            else {
                this._fetchMembers();
            }
        }
        if (this.members?.[this.currentOrg]?.[`page_${this.page}`]) {
            this._memberData = this.members[this.currentOrg][`page_${this.page}`];
            this._loading = false;
        }
        this.renderRoot.querySelector('button,a')?.focus();
        //console.log(this.member_lookup_texts);
        cardLinks(this.shadowRoot);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.querySelector('.wp-block-ace-content')?.classList.remove('switch-to-one-column');
        this.eventManager.disconnect();
    }
    async updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('currentOrg') && this.currentOrg) {
            this.organisation = this._getOrganisationById(this.currentOrg);
            if (this.organisation && this.organisation.hasOwnProperty('members') && this.organisation.members.hasOwnProperty(`page_${this.page}`)) {
                this._memberData = this.organisation.members[`page_${this.page}`];
                this._loading = false;
            }
            if (this.currentOrg) {
                this._maybeGetMembersFromSessionStorage();
                if (this.organisation?.members?.[`page_${this.page}`]) {
                    await this._fetchMembers();
                }
                else {
                    this._fetchMembers();
                }
            }
        }
        if ((changedProperties.has('currentOrg') && this.currentOrg) || (changedProperties.has('members') && this.members)) {
            if (this.organisation?.members?.[`page_${this.page}`]) {
                this._memberData = this.organisation.members[`page_${this.page}`];
                this._loading = false;
            }
        }
    }
    render() {
        let main = '';
        if (this._loading) {
            main = html `<allin-loader loading></allin-loader>`;
        }
        else {
            let table = html `<p>No members have shared their profile with you</p>`;
            let search = '';
            let data = this._memberData;
            const memberLookupText = this.member_lookup_texts;
            const flavourText = memberLookupText.member_lookup_flavour_text;
            const searchHeading = memberLookupText.member_lookup_search_heading;
            const browseHeading = memberLookupText.member_lookup_browse_profile_heading;
            const browseDescription = memberLookupText.member_lookup_browse_profiles_description;
            const searchByText = memberLookupText.member_lookup_search_by_text;
            if (this._hasSearch) {
                data = this._searchResults;
            }
            if (data?.length || this._hasSearch) {
                search = html `\n                    <div class="dashboard-flavour-text">\n                        ${unsafeHTML(flavourText)}\n                    </div>\n                    <h3>${searchHeading || `Search members`}</h3>\n                    <allin-members-search\n                            restUrl=${this.restUrl}\n                            nonce=${this.nonce}\n                            .searchByText=${searchByText}\n                        ></allin-members-search>`;
                if (!data?.length && this._hasSearch) {
                    table = html `<div class="flex space-between wrap"><p>No members found matching your search criteria</p><button class="button" @click=${this._handleClearSearch}>Reset</button></div>`;
                }
                else {
                    let heading = this._hasSearch ? `Search results` : `Browse profiles shared`;
                    let para = this._hasSearch ? `Members matching your search criteria` : `Browse members who have shared their profile with you.`;
                    let button = this._hasSearch ? html `<button class="button" @click=${this._handleClearSearch}>Clear search</button>` : '';
                    table = html `\n                        <div class="flex space-between wrap">\n                            <div>\n                                <h3>${heading}</h3>\n                                <p>${para}</p>\n                            </div>\n                            ${button}\n                        </div>\n                        <div role="region" aria-labelledby="table_caption" tabindex="0" class="scroll-auto">\n                            <style>${this._styles?.join('')}</style>\n                            <table id="members_table">\n                                <caption id="table_caption" class="screen-reader-text">Browse members who have shared their profile with you.</caption>\n                                <tbody>\n                                <tr>\n                                    <th>First name</th>\n                                    <th>Last name</th>\n                                    <th>Email address</th>\n                                    <th>Member Number</th>\n                                    <th><span class="screen-reader-text">Actions</span></th>\n                                </tr>\n                                ${data.map((member) => {
                        let { id, firstName, lastName, email, allInMemberNumber } = member;
                        let buttonGroup = html `\n                                        <a href="javascript:void(0)" data-member_id="${allInMemberNumber}" data-member_surname="${lastName}" data-org_id="${this.currentOrg}" @click=${this._handleMemberClick}>View access details</a>\n                                    `;
                        return html `\n                                        <tr>\n                                            <td>${firstName}</td>\n                                            <td>${lastName}</td>\n                                            <td>${email}</td>\n                                            <td>${allInMemberNumber}</td>\n                                            <td>${buttonGroup}</td>\n                                        </tr>\n                                    `;
                    })}\n                                </tbody>\n                            </table>\n                        </div>\n                    `;
                }
            }
            let shouldPaginate = this.organisation?.members?.total > 9;
            let paginate = '';
            if (shouldPaginate && !this._searchResults?.length) {
                paginate = html `\n                    <allin-pagination\n                        total="${this.organisation.members.total}"\n                        pageSize=9\n                        name=${this.name}\n                        currentPage=${this.page}\n                    />\n\n                `;
            }
            main = html `\n                <div class="grid">\n                    <div class="flex header">\n                        <div class="return">                  \n                            <button @click=${(e) => { e.preventDefault(); this._updateContext({ dashboard: 'dashboard' }); }} aria-label="Back to dashboard"><span aria-hidden=true>${arrowBack}</span>Back to dashboard</button>\n                        </div>\n                    </div>\n                    ${search}\n                    ${table}\n                    ${paginate}\n                </div>\n            `;
        }
        return html `${main}`;
    }
    _getOrganisationById(id) {
        return this.organisations.find((org) => org.org_id == id);
    }
    _handleMemberClick(e) {
        e.preventDefault();
        this._updateContext({ panel: `${e.target.dataset.member_id}__${e.target.dataset.member_surname}` });
    }
    _handleChangePage(e) {
        const detail = e.detail;
        if ('page' in detail && detail.page !== this.page) {
            this.page = detail.page;
            this._fetchMembers();
        }
    }
    _handleSearchResults(e) {
        const detail = e.detail;
        if ('results' in detail) {
            this._searchResults = detail.results;
            this._hasSearch = true;
            if (detail.type === 'id') {
                this.membersLookedUp = this.membersLookedUp || {};
                this.membersLookedUp[this.currentOrg] = this.membersLookedUp[this.currentOrg] || {};
                this.membersLookedUp[this.currentOrg][detail.results[0].allInMemberNumber] = detail.results[0];
                this._updateContext({ membersLookedUp: this.membersLookedUp });
            }
        }
    }
    _handleClearSearch() {
        this._searchResults = [];
        this._hasSearch = false;
        this.eventManager.emit(`allin-search-members-clear-${this.name}`);
    }
    async _fetchMembers() {
        this._loading = true;
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-members&nonce=${this.nonce}&page=${this.page}`;
        try {
            let response = await fetch(url, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
            let data = await response.json();
            if (!this.members?.[this.currentOrg]) {
                this.members = this.members || {};
                this.members[this.currentOrg] = {};
            }
            this.members[this.currentOrg][`page_${this.page}`] = data.members.data;
            data.members.data.forEach((member) => {
                this.members[this.currentOrg][member.id] = member;
            });
            this.members[this.currentOrg].total = data.members.total;
            this._setMembersToSessionStorage(this.members);
            this._updateContext({ members: this.members });
        }
        catch (error) {
            console.error('Error:', error);
        }
        finally {
            this._loading = false;
        }
    }
    static styles = [
        ...super.styles,
        accessibility,
        grid,
        css `
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
            margin-bottom:1rem;
            
        }
        @media screen and (min-width:700px){
            .bg{
                grid-template-columns: auto 1fr 2rem;
            }
        }
        .button{
            text-align:center;
        }
        .grid{
            gap:1.5rem;
        }
        
        .card a{
            display:block;
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            background-color:var(--allin-highlight-color,#472961);
            color:#fff;
            text-align:center;
            font-weight:700;
            text-decoration:none;
            transition:background-color var(--transition) ease;
            height:fit-content;
            align-self:end;
        }

        .card:hover a{
            background-color:var(--allin-highlight-color-lighter,#5e3e7d);
        }

        .content{
            display:grid;
            gap:0.5rem;
        }
        .card img{
            object-fit:contain;
        }
        h4,h3,p{
            margin:0;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            gap:1rem;
        }

        .button-group button svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce) {
            .button-group button svg{
                transform: none !important;
            }
        }
        
        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        ul li{
            display:grid;
            gap:0.5rem;
        }

        .button{
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            font-family:var(--allin-font-family,'Montserrat');
            background-color:var(--allin-button-bg-color,#472961);
            font-size:var(--allin-button-font-size,1rem);
            color:var(--allin-button-text-color,#fff);
            font-weight:(var(--allin-button-font-weight,600));
            cursor:pointer;
            border:none;
            text-decoration:none;
            transition:background-color var(--transition) ease;
        }

        .header{
            justify-content:space-between;
            max-width:1300px;
        }

        .return button{
            color:var(--allin-button-bg-color,#472961);
            border:none;
            background-color:transparent;
            padding:0;
            display:flex;
            align-items:center;
            gap:0.5rem;
            font-weight:400;
            font-size:1.25rem;
            cursor:pointer;
        }

        table{
            width:100%;
        }

        table a{
            text-decoration:underline;
            color:var(--allin-link-color,#472961);
            display:block;
            font-weight:700;
            margin-bottom:0.5rem;
        }
        
        #events_table td:nth-child(1)::before, #events_table td:nth-child(6)::before{
            display:none;
        }

        @media screen and (max-width:1000px){
            #events_table td:has(img){
                aspect-ratio:16/9;
                position:relative;
            }
            #events_table td:has(img) img{
                position:absolute;
                top:0;
                left:0;
                width:100%;
                height:100%;
                object-fit:cover;
            }

            #events_table td:not(:first-of-type):not(:nth-of-type(6)){
                display:grid;
                gap:0.5rem;
                grid-template-columns: 10ch 1fr;
                padding:0;
            }
            #events_table td{
                padding:0;
            }
            #events_table tr:not(:first-of-type){
                display:grid;
                gap:1rem;
                grid-template-rows:auto auto auto auto auto 1fr;
                padding:2rem;
            }
            #events_table td img{
                object-fit: cover;
            }
        }

        allin-members-search .bg{
            display:block;
        }
        allin-members-search ul{
            padding:0;
            margin:1rem 0;
            list-style:disc;
        }

        allin-members-search ul li{
            margin-bottom:0.5rem;
            display:list-item;
            align-items:center;
            padding-left: 1rem;
            a{
                color:var(--allin-link-color,#472961);
                font-weight:700;
            }
        }
    
        .searchboxes {
            display: grid;
            grid-template-columns: 1fr auto;
            width: 100%;
            max-width: 800px;
            margin-bottom: 2rem;
            position:relative;
        }

        .searchbox {
            position: relative;
            label {
                margin-bottom: 0.5rem;
                display:block;
            }
        }
        .searchbox svg {
            position: absolute;
            left: 0.5rem;
            bottom: 0.8rem;
            width: 1.5rem;
            height: 1.5rem;
        }

        .searchbox input{
            padding:1rem;
            padding-left:2.5rem;
            border:1px solid var(--allin-border-color,#707070);
            background-color:var(--color-white);
            border-radius:0.5rem 0 0 0.5rem;
            width:100%;
            box-sizing: border-box;
            font-family:var(--allin-font-family,'Montserrat');
        }
        .searchbox:has(:nth-child(3)){
            input:nth-child(2){
                border-radius:0.5rem 0.5rem 0.5rem 0.5rem;
                margin-bottom:0.5rem;
                display:block;
            }
        }
        @media screen and (min-width:700px){
            .searchbox:has(:nth-child(3)){
                display:grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: auto auto;
                column-gap:0.25rem;
                input{
                    grid-row:2/3;
                    &:nth-child(2){
                        border-radius:0.5rem 0 0 0.5rem;
                        margin-bottom:0;
                    }
                    &:nth-child(4){
                        border-radius:0 0 0 0;
                    }
                }
                label{
                    grid-row:1/2;
                }
            }
        }
        .searchboxes button{
            border-radius:0 0.5rem 0.5rem 0;
            height:calc(3rem + 2px);
            align-self:end;
        }

        form{
            display:flex;
            flex-direction:column;
            gap:1rem;
            position:relative;
        }
        .loading .searchboxes::after{
            content:'';
            position:fixed;
            top:calc(50% - 1rem);
            left:calc(50% - 1rem);
            width:2rem;
            height:2rem;
            background-image:url(/wp-content/themes/ace/assets/wheel.svg);
            z-index:100;
            background-size: cover;
            animation:spin 1s linear infinite;
        }
        @keyframes spin{
            from{
                transform:rotate(0deg);
            }
            to{
                transform:rotate(-360deg);
            }
        }

        .button{
            width:fit-content;
            margin-left:auto;
            display:block;
        }

        .hide{
            display:none;
        }

        .clear{
            background-color:transparent;
            color:var(--allin-link-color,#472961);
            font-weight:700;
            border:none;
            text-decoration:underline;
            cursor:pointer;
            fonot-size:1.25rem;
        }
        `
    ];
}
